import React, { useState } from "react";
import { StyleSheet, View, Platform, Text, Dimensions } from "react-native";
import LHSectionHeader from "../../components/LHSectionHeader";
import LHOptionCard from "../../components/LHOptionCard";
import SparcWalkInspectionImageUpload from "./SparcWalkInspectionImageUpload";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import FontFamily from "../../constants/fonts";
import { useTheme } from "../../theme";

// Helper to detect iPad
const isIPad = Platform.OS === 'ios' && Dimensions.get('window').width >= 768;

type Option = {
  id?: string | number;
  ID?: string | number;
  QuestionId?: string | number;
  label?: string;
  Question?: string;
  Behaviour?: string;
  Details?: string | null;
  Detail_Link?: string | null;
};

type Props = {
  title: string;
  options?: Option[];
  selectedIds?: string[];
  onSelect?: (id: string) => void;
  error?: boolean;
  isDisabled?: boolean;
  testID?: string;
};

const SparcWalkInspectionActivity: React.FC<Props> = ({
  title,
  options = [],
  selectedIds = [],
  onSelect,
  error = false,
  isDisabled = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const [imageUploadModalVisible, setImageUploadModalVisible] =
    useState<boolean>(false);
  const [selectedActivity, setSelectedActivity] = useState<Option | null>(null);
  const sparcImages = useSelector(
    (state: RootState) => state.sparcWalkInspection.images
  );

  const getOptionId = (opt: Option) =>
    (opt?.QuestionId ?? opt?.ID ?? opt?.id)?.toString?.() ?? "";

  const handleSelect = (id: string) => {
    if (onSelect) {
      onSelect(id);
    }
  };

  const handleCameraPress = (option: Option): void => {
    setSelectedActivity(option);
    setImageUploadModalVisible(true);
  };

  const hasUploadedImages = (optionId: string): boolean =>
    sparcImages.some((img) => img.observationId === optionId);

  const getDisplayTitle = (opt: Option): string =>
    opt.Question || opt.Behaviour || opt.label || "Untitled";

  const getDetailsText = (opt: Option): string =>
    opt.Details || opt.Detail_Link || "";

  // Calculate justifyContent based on number of cards per row
  const getJustifyContent = (): "flex-start" | "center" => {
    const totalOptions = options.length;
    
    // On iOS iPad, always use flex-start (all cards start from left)
    if (isIPad) {
      return "flex-start";
    }
    
    if (Platform.OS === "web") {
      // On web, with flexWrap and gap, cards will naturally flow
      // Use flex-start for 1-2 cards, center only for 3+ cards
      const cardsPerRow = Math.floor((100 - 32) / (200 + 16)); // Approximate cards per row calculation
      const lastRowCards = totalOptions % cardsPerRow;
      
      // Use center only when we have 3 or more cards in a row
      return lastRowCards >= 3 || (cardsPerRow >= 3 && totalOptions >= 3) ? "center" : "flex-start";
    } else {
      // On mobile iOS (non-iPad), use flex-start for 1-2 cards, center only for 3+ cards
      return totalOptions >= 3 ? "center" : "flex-start";
    }
  };

  return (
    <View style={[styles.container, error && styles.errorContainer]}>
      {/* Card with integrated header and body */}
      <View style={styles.cardContainer}>
        {/* Header Section */}
        <LHSectionHeader
          title={title}
          containerStyle={styles.sectionHeader}
          textStyle={styles.header1text}
        />

        {/* Body Section */}
        <View
          style={[
            styles.optionGrid,
            styles.optionCardWrapper,
            isDarkTheme && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
            },
            { justifyContent: getJustifyContent() },
          ]}
        >
          {options.map((opt) => {
            const optionId = getOptionId(opt);
            const hasImages = hasUploadedImages(optionId);

            return (
              <LHOptionCard
                key={optionId}
                title={getDisplayTitle(opt)}
                selected={selectedIds.includes(optionId)}
                disabled={isDisabled}
                onPress={() => !isDisabled && handleSelect(optionId)}
                onCameraPress={() => handleCameraPress(opt)}
                hasUploadedImage={hasImages}
                detailsText={getDetailsText(opt)}
              />
            );
          })}
        </View>
      </View>

      {error && (
        <Text style={styles.errorText}>Please select at least one option</Text>
      )}

      {/* Image Upload Modal */}
      {selectedActivity && (
        <SparcWalkInspectionImageUpload
          visible={imageUploadModalVisible}
          onClose={() => setImageUploadModalVisible(false)}
          activityId={getOptionId(selectedActivity)}
          activityLabel={getDisplayTitle(selectedActivity)}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginTop: 24,
  },
  errorContainer: {
    borderColor: "#dc3545",
    borderWidth: 1,
  },
  cardContainer: {
    borderRadius: 6,
    // allow header to expand beyond original bounds when wrapping
    overflow: Platform.OS === "ios" ? "visible" : "hidden",
  },
  sectionHeader: {
    backgroundColor: "#3582E6",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    // use minHeight so header can expand to 2 lines when needed
    minHeight: 48,
  },
  header1text: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 20,
    fontFamily: FontFamily.bold,
  },
  optionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    // justifyContent is now set dynamically based on number of cards per row
    gap: Platform.OS === "web" ? 16 : 6,
    borderColor: "#e9ecef",
    borderWidth: 1,
    borderTopWidth: 0,
    borderRadius: 6,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    padding: 16,
  },
  optionCardWrapper: {
    backgroundColor: "white",
  },
  errorText: {
    color: "#dc3545",
    fontSize: 12,
    marginTop: 4,
    marginLeft: 16,
    fontFamily: FontFamily.regular,
  },
});

export default SparcWalkInspectionActivity;
